import images from '@assets/images/index'
import { Row, SortingState } from '@tanstack/react-table'
// import { compareDesc, parseISO, isValid, format } from 'date-fns'
import {
  commonActionColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { Flex } from 'antd'
import { compareDesc } from 'date-fns'

import { Link } from '@suntex.core/nav'
import {
  Database_Procs_SchoolSummaryByTeam_Item,
  Entities_Assessment_TeamSummary_PlayerSummary
} from '@suntex.fim/api-client'
import { ColumnType, RowData } from '@suntex.fim/ui'

// import { toZonedTime } from 'date-fns-tz'
import {
  generateCommonColumns,
  generateCommonColumnsForSchool
} from './CommonColumns'

export type SummaryReportData =
  | Entities_Assessment_TeamSummary_PlayerSummary
  | (Database_Procs_SchoolSummaryByTeam_Item &
      Partial<Entities_Assessment_TeamSummary_PlayerSummary>)

export const teamSummaryInitialSorting: SortingState = [
  { id: 'displayName', desc: false }
]
export const schoolSummaryInitialSorting: SortingState = [
  { id: 'teamName', desc: false }
]

export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  userIdNumber: 'ID',
  userFirstName: 'First Name',
  userLastName: 'Last Name',
  estTimeOnTask: 'Est. Time on Task (H)',
  estProblemsSolved: 'Est. Problems Solved',
  lastActivity: 'Last Activity'
}

export function createColumn<T extends RowData>(
  header: string,
  id: string,
  accessor?: keyof T | ((row: T) => unknown),
  size = 50,
  cell?: (info: any) => React.ReactNode,
  additionalProps = {},
  hiddenFromUI?: boolean // Optional property to hide columns from UI
): ColumnType<T> & { hiddenFromUI?: boolean } {
  return {
    header: () => renderHeaderTooltip(header),
    id,
    ...(typeof accessor === 'string'
      ? { accessorKey: accessor }
      : { accessorFn: accessor }),
    cell,
    size,
    hiddenFromUI, // Ensure TypeScript compliance
    ...additionalProps
  }
}
// const formatLastActivity = (
//   dateInput: string | Date | null | undefined
// ): string => {
//   if (!dateInput || dateInput === '0001-01-01T00:00:00') {
//     return 'None'
//   }

//   try {
//     // Normalize to Date object
//     const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput

//     if (!isValid(date)) return 'Invalid'

//     const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
//     const zonedDate = toZonedTime(date, timeZone)

//     const formattedDate = format(zonedDate, "MMMM d, yyyy '@' h:mm:ss a")
//     return formattedDate
//   } catch (error) {
//     // eslint-disable-next-line no-console
//     console.error('Error formatting last activity date:', error)
//     return 'Error'
//   }
// }

export function columns<T extends SummaryReportData>({
  sendMessage
}: {
  sendMessage: (t: Row<T>[]) => void
}): ColumnType<T>[] {
  return [
    commonActionColumn<T>(hidableSelectLabel, sendMessage),
    ...generateCommonColumns<T>(),
    createColumn<T>('ID', 'userIdNumber', 'userIdNumber', 50, (info) =>
      info.getValue()?.toLocaleString()
    ),
    createColumn<T>(
      'First Name',
      'userFirstName',
      'userFirstName',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<T>(
      'Last Name',
      'userLastName',
      'userLastName',
      50,
      (info) => info.getValue()?.toLocaleString(),
      {
        enableGlobalFilter: true
      }
    ),
    createColumn<T>(
      'Est. Time on Task (H)',
      'estTimeOnTask',
      'estTimeOnTask',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<T>(
      'Est. Problems Solved',
      'estProblemsSolved',
      'estProblemsSolved',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    {
      header: () => renderHeaderTooltip('Last Activity'),
      id: 'lastActivity',
      accessorKey: 'lastActivity',
      accessorFn: ({ lastActivity }) => {
        // const date = new Date(lastActivity as string)
        // return lastActivity ? formatLastActivity(lastActivity) : 'no activity'
        // const date = new Date(lastActivity as string)
        // return lastActivity ? date.toLocaleString() : 'no activity'
        return lastActivity ? lastActivity.toLocaleString() : 'no activity'
      },
      size: 50,
      sortingFn: (aData, bData) => {
        const aDate = aData.original.lastActivity
        const bDate = bData.original.lastActivity
        if (!aDate) {
          return -1
        }
        if (!bDate) {
          return 1
        }
        return compareDesc(new Date(aDate), new Date(bDate))
      }
    }
  ]
}

export const schoolHidableSelectLabel: Record<string, string> = {
  teamName: 'Name',
  teamClassName: 'Class Name',
  contactName: 'Teacher Name',
  teamGrade: 'Grade',
  students: 'Students',
  stickers: 'Stickers',
  stickersPerStudent: 'Stickers per Student',
  userId: 'Teacher User Id',
  timeOnTask: 'Est. Time on Task (H)',
  problemsSolved: 'Est. Problems Solved',
  mathematicians: 'Mathematicians',
  percentMathematicians: '% Mathematicians',
  grandChampions: 'Grand Champions',
  percentGrandChampions: '% Grand Champions'
}

export const schoolColumns =
  (): ColumnType<Database_Procs_SchoolSummaryByTeam_Item>[] => [
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      '',
      'teamId',
      'teamId',
      50,
      (info) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const value = info.getValue()
        return (
          <Flex justify="center">
            {value ? (
              <Link to={`/team/${value}/dashboard`}>
                <images.MessageIcon />
              </Link>
            ) : (
              'None'
            )}
          </Flex>
        )
      },
      { enableSorting: false, enableGlobalFilter: false }
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Name',
      'teamName',
      'teamName',
      50,
      (info) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const value = info.getValue()
        return (
          <b>
            {value ? (
              <Link
                to={`/team/${info.row.original.teamId}/assessment-report/summary`}
              >
                {value}
              </Link>
            ) : (
              'None'
            )}
          </b>
        )
      }
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Class Name',
      'teamClassName',
      'teamClassName',
      50,
      (info) => <b>{info.getValue() || 'None'}</b>
    ),
    // Ensure schoolCommonColumns is compatible with Database_Procs_SchoolSummaryByTeam_Item
    ...generateCommonColumnsForSchool<Database_Procs_SchoolSummaryByTeam_Item>().slice(
      2
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Stickers per Student',
      'stickersPerStudent',
      'stickersPerStudent',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    // createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
    //   'Teacher Email',
    //   'contactEmail',
    //   'contactEmail',
    //   50,
    //   (info) => info.getValue()?.toLocaleString(),
    //   {},
    //   true
    // ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Teacher User Id',
      'userId',
      'userId',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Est. Time on Task (H)',
      'timeOnTask',
      'timeOnTask',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Est. Problems Solved',
      'problemsSolved',
      'problemsSolved',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Mathematicians',
      'mathematicians',
      'mathematicians',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      '% Mathematicians',
      'percentMathematicians',
      'percentMathematicians',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      'Grand Champions',
      'grandChampions',
      'grandChampions',
      50,
      (info) => info.getValue()?.toLocaleString()
    ),
    createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
      '% Grand Champions',
      'percentGrandChampions',
      'percentGrandChampions',
      50,
      (info) => info.getValue()?.toLocaleString()
    )
    // createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
    //   'Start Date',
    //   'teamProgramBegins',
    //   'teamProgramBegins',
    //   50,
    //   (info) => info.getValue()?.toLocaleString(),
    //   true
    // ),
    // createColumn<Database_Procs_SchoolSummaryByTeam_Item>(
    //   'End Date',
    //   'teamProgramEnds',
    //   'teamProgramEnds',
    //   50,
    //   (info) => info.getValue()?.toLocaleString(),
    //   true
    // )
  ]
